import React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { atoms, colors, Primitive, Text } from '@rtkwlf/fenrir-react';
import { faSort } from '@rtkwlf/pro-solid-svg-icons/faSort';
import { faSortDown } from '@rtkwlf/pro-solid-svg-icons/faSortDown';
import { faSortUp } from '@rtkwlf/pro-solid-svg-icons/faSortUp';

import { TABLE_KEYS } from './table.constants';
import type { HeaderColumn } from './table.types';

const Th = styled.th<{ width?: number | string }>`
  ${({ width }) => css`
    white-space: nowrap;
    width: ${width ? `${width}%` : 'auto'};
  `}

  @media print {
    svg[data-icon^='sort'] {
      display: none;
    }
  }
`;

const cellClassName = atoms({
  borderBottomWidth: 'large',
  borderColor: 'neutralDefault',
  borderStyle: 'solid',
  borderWidth: 'zero',
  fontWeight: 'bold',
  margin: 'zero',
  paddingBottom: 'default',
  paddingLeft: 'default',
  paddingRight: 'default',
  paddingTop: 'small',
  textAlign: 'left',
  textTransform: 'capitalize',
  wordBreak: 'break-word',
});

export const rightStickyStyle = css`
  position: sticky;
  right: 0;
  background-color: rgb(${colors.background.widget.default});
  box-shadow: -10px 0 8px -8px rgba(${colors.mono.black}, 0.05);
  text-align: center;
`;

export const TableHeader: React.FC<{
  headerGroups: HeaderColumn[];
}> = ({ headerGroups }) => (
  <thead>
    {headerGroups.map((headerGroup) => {
      const trProps = headerGroup.getHeaderGroupProps();
      return (
        <tr {...trProps} key={trProps.key}>
          {headerGroup.headers.map((column: HeaderColumn) => {
            const thProps = column.getHeaderProps(
              column.sortable
                ? column.getSortByToggleProps({ title: undefined })
                : undefined,
            );

            return (
              <Th
                {...thProps}
                width={column.width}
                className={cellClassName}
                css={column.stickyStyle ?? css``}
                key={thProps.key}
              >
                <Text styledAs="body4">
                  <>
                    {column.render(TABLE_KEYS.HEADER)}
                    {/* Add a sort direction indicator */}
                    {column.sortable ? (
                      <Primitive.span marginLeft="small">
                        {column.isSorted ? (
                          <FontAwesomeIcon
                            color="var(--aw-palette-temperature-tempGrey2)"
                            icon={column.isSortedDesc ? faSortDown : faSortUp}
                          />
                        ) : (
                          <FontAwesomeIcon
                            color="var(--aw-palette-temperature-tempGrey2)"
                            icon={faSort}
                          />
                        )}
                      </Primitive.span>
                    ) : null}
                  </>
                </Text>
              </Th>
            );
          })}
        </tr>
      );
    })}
  </thead>
);
